import { ModalsProvider } from "@mantine/modals"
import { StringtaleProvider } from "@stringtale/react"
import { QueryClientProvider } from "@tanstack/react-query"
import "@total-typescript/ts-reset"
import FilePathProvider from "app/files/components/FilePathProvider"
import { PlayerProvider } from "app/radio/components/PlayerContext"
import { RadioRouteGuard } from "app/radio/components/RadioRouteGuard"
import { shouldHideRadio } from "app/radio/utils/shouldHideRadio"
import Toasts from "app/toasts/components/Toasts"
import { ToastProvider } from "app/toasts/hooks/useToast"
import { queryClient, trpcClient } from "app/trpc"
import type { AppProps } from "next/app"
import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { ErrorBoundary } from "react-error-boundary"
import { poppins } from "theme/fonts"

import { CacheProvider } from "@emotion/react"
import { MantineProvider } from "@mantine/core"
import { emotionTransform, MantineEmotionProvider } from "@mantine/emotion"
import RootErrorFallback from "app/core/components/RootErrorFallback"
import { SystemInfoProvider } from "app/core/providers/SystemInfoProvider"
import { UserInfoProvider } from "app/core/providers/UserInfoProvider"
import { TRPCProvider } from "app/trpc-context"
import { NuqsAdapter } from "nuqs/adapters/next/pages"
import { ReactNode, Suspense, useEffect, useState } from "react"
import { LoadingOverlay } from "ui/LoadingContainer"
import { emotionCache } from "web/emotion/cache"
import EnvProviderPages from "web/src/core/components/EnvProviderPages"
import "../src/global.css"

export default function App({ Component, pageProps, router }: AppProps) {
  const [isClient, setIsClient] = useState(false)
  useEffect(() => setIsClient(true), [])

  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: "https://ph.123zing.nl",
      ui_host: "https://eu.posthog.com",
      capture_pageview:
        process.env.NEXT_PUBLIC_CAPTURE_PAGEVIEWS === "history_change"
          ? "history_change"
          : process.env.NEXT_PUBLIC_CAPTURE_PAGEVIEWS === "true"
            ? true
            : false,
      capture_exceptions:
        process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_EXCEPTIONS === "true"
          ? true
          : false,
      loaded: (posthog) => {
        if (process.env.NODE_ENV === "development") posthog.debug()
      },
      autocapture:
        process.env.NEXT_PUBLIC_POSTHOG_AUTOCAPTURE === "true" ? true : false,
      debug: process.env.NODE_ENV === "development",
    })
  }, [])

  const getLayout = (Component as any).getLayout || ((page: ReactNode) => page)

  //Hide radio on admin, kids, and lesson/rehearsal pages.
  const hideRadio = shouldHideRadio(router.pathname)

  if (!isClient) {
    return <LoadingOverlay />
  }

  return (
    <EnvProviderPages>
      <CacheProvider value={emotionCache}>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <Suspense fallback={<LoadingOverlay />}>
              <PostHogProvider client={posthog}>
                <MantineEmotionProvider>
                  <MantineProvider stylesTransform={emotionTransform}>
                    <FilePathProvider>
                      <SystemInfoProvider>
                        <NuqsAdapter>
                          <ToastProvider>
                            <ModalsProvider>
                              <PlayerProvider>
                                <RadioRouteGuard hideRadio={hideRadio} />
                                <Toasts />

                                <StringtaleProvider locales="nl">
                                  <main className={`${poppins.className}`}>
                                    <ErrorBoundary
                                      FallbackComponent={RootErrorFallback}
                                      resetKeys={[router.asPath]}
                                      onReset={() => {
                                        queryClient.clear()
                                      }}
                                    >
                                      {/* UserInfoProvider must be inside the ErrorBoundary to catch errors */}
                                      <UserInfoProvider>
                                        {getLayout(
                                          <Component {...pageProps} />
                                        )}
                                      </UserInfoProvider>
                                    </ErrorBoundary>
                                  </main>
                                </StringtaleProvider>
                              </PlayerProvider>
                            </ModalsProvider>
                          </ToastProvider>
                        </NuqsAdapter>
                      </SystemInfoProvider>
                    </FilePathProvider>
                  </MantineProvider>
                </MantineEmotionProvider>
              </PostHogProvider>
            </Suspense>
          </TRPCProvider>
        </QueryClientProvider>
      </CacheProvider>
    </EnvProviderPages>
  )
}
